import React from "react"
import styled, { css } from "styled-components"
import SkeletonLoader from "../../../../components/SkeletonLoader"
import { IconChapa } from "../../../../components/Icons"

const tableColumns = "minmax(64px, 0.75fr) minmax(0, 2.2fr) minmax(0, 1fr) minmax(84px, 0.9fr)";

const RankingWrapper = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 20px auto;
  font-family: "Poppins", sans-serif;
  box-sizing: border-box;

  @media (max-width: 1024px) {
    overflow-x: auto;
  }

  @media (max-width: 768px) {
    margin: 12px auto;
  }
`

const RankingHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 0.5fr;
  justify-content: space-between;
  align-items: center;
  border-bottom: 2px solid #000;
  margin-bottom: 10px;
  padding: 12px 0;
  box-sizing: border-box;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`

const Title = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;

  @media (max-width: 768px) {
    overflow-wrap: anywhere;
  }
`

const RankingCount = styled.span`
  font-size: 14px;
  font-weight: normal;
  color: #555;
`

const CountrySelect = styled.select`
  min-width: 0;
  padding: 6px 28px 6px 10px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 14px;
  background-color: #fff;
  appearance: none;
  background-image: url("data:image/svg+xml;utf8,<svg fill='black' height='14' viewBox='0 0 24 24' width='14' xmlns='http://www.w3.org/2000/svg'><path d='M7 10l5 5 5-5z'/></svg>");
  background-repeat: no-repeat;
  background-position: right 8px center;
  background-size: 14px;

  @media (max-width: 768px) {
    width: 100%;
    max-width: 100%;
  }
`

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: ${tableColumns};
  font-weight: 600;
  padding: 8px 0;
  border-bottom: 1px solid #ddd;
  color: #444;
  font-size: 14px;
  column-gap: 12px;
  box-sizing: border-box;

  > span {
    min-width: 0;
    overflow-wrap: anywhere;
  }

  @media (max-width: 1024px) {
    column-gap: 8px;
    padding: 8px 6px;
  }
`

const RankingRow = styled.div`
  display: grid;
  grid-template-columns: ${tableColumns};
  align-items: center;
  padding: 10px;
  font-size: 14px;
  column-gap: 12px;
  box-sizing: border-box;

  @media (max-width: 1024px) {
    column-gap: 8px;
    padding: 10px 6px;
  }

  ${({ $even }) =>
        $even &&
        css`
      background: #f9f9f9;
    `}

  ${({ $odd }) =>
        $odd &&
        css`
      background: #fff;
    `}

  ${({ $highlight }) =>
        $highlight &&
        css`
      background: #000;
      color: #fff;
      font-weight: 600;
      border-radius: 10px;
    `}
`

const Col = styled.div`
  min-width: 0;
  padding: 4px 6px;
  overflow-wrap: anywhere;
  word-break: normal;

  @media (max-width: 768px) {
    padding: 4px 2px;
  }
`

const Name = styled(Col)`
  font-weight: 600;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 768px) {
    white-space: normal;
    overflow: visible;
    text-overflow: initial;
    overflow-wrap: anywhere;
  }
`

const Points = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
  min-width: 0;
  max-width: 100%;
  overflow-wrap: anywhere;

  svg {
    flex: 0 0 auto;
  }
`

const ErrorMessage = styled.div`
  padding: 16px;
  margin: 20px 0;
  background-color: #fee;
  border: 1px solid #fcc;
  border-radius: 8px;
  color: #c33;
  text-align: center;
`;

const EmptyMessage = styled.div`
  padding: 40px 16px;
  text-align: center;
  color: #888;
  font-size: 14px;
  grid-column: 1 / -1;
`;

export default function RankingDesktop({
    data,
    total,
    position,
    countries,
    selectedCountry,
    onCountryChange,
    loading = false,
    error = null,
    hideCountrySelect = false,
}) {
    return (
        <RankingWrapper>
            <RankingHeader>
                <Title>
                    Tu Ranking{" "}
                    {loading ? (
                        <SkeletonLoader active width="80px" height="20px" display="inline-block" margin="0 0 0 5px" />
                    ) : (
                        <RankingCount>
                            ({position}/{total})
                        </RankingCount>
                    )}
                </Title>
                {!hideCountrySelect && (
                    <CountrySelect
                        value={selectedCountry}
                        onChange={(e) => onCountryChange(e.target.value)}
                        disabled={loading}
                    >
                        {countries.map((c) => (
                            <option key={c.value} value={c.value}>
                                {c.label}
                            </option>
                        ))}
                    </CountrySelect>
                )}
            </RankingHeader>

            <TableHeader>
                <span>Posición</span>
                <span>Nombre</span>
                <span>Pais</span>
                <span>Valoración</span>
            </TableHeader>

            {error && (
                <ErrorMessage>
                    ⚠️ {error}
                </ErrorMessage>
            )}

            {loading && (
                <>
                    {[...Array(10)].map((_, idx) => (
                        <RankingRow key={`skeleton-${idx}`} $even={idx % 2 === 0} $odd={idx % 2 !== 0}>
                            <Col>
                                <SkeletonLoader active width="40px" height="16px" />
                            </Col>
                            <Col>
                                <SkeletonLoader active width="120px" height="16px" />
                            </Col>
                            <Col>
                                <SkeletonLoader active width="80px" height="16px" />
                            </Col>
                            <Col>
                                <SkeletonLoader active width="60px" height="16px" />
                            </Col>
                        </RankingRow>
                    ))}
                </>
            )}

            {!loading && !error && data.length === 0 && (
                <EmptyMessage>
                    No hay datos de ranking disponibles
                </EmptyMessage>
            )}

            {!loading && !error && data.map((item, idx) => {
                return (
                    <RankingRow
                        key={item._id || idx}
                        $highlight={item.isCurrent}
                        $even={idx % 2 === 0}
                        $odd={idx % 2 !== 0}
                    >
                        <Col>{item.pos || idx + 1}</Col>
                        <Name>{item.name || "Sin nombre"}</Name>
                        <Col>{item.country || "-"}</Col>
                        <Col>
                            <Points>
                                <IconChapa width="24" height="24" />
                                {item.points || 0}
                            </Points>
                        </Col>
                    </RankingRow>
                )
            })}
        </RankingWrapper>
    )
}
